import { Auth0Provider } from "@auth0/auth0-react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { isNativePlatform } from "@/lib/native";

import { authRedirectUri, useNativeAuthCallback } from "./nativeAuth";
import { useAuthSessionSync } from "./sessionSync";

const domain = import.meta.env.VITE_AUTH0_DOMAIN;
const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
const audience = import.meta.env.VITE_AUTH0_AUDIENCE;

interface Props {
  children: ReactNode;
}

/** Bridges native Auth0 deep-link callbacks into the SDK. Renders nothing. */
function NativeAuthBridge() {
  useNativeAuthCallback();
  return null;
}

/** Signs this window out when another same-origin window signs out. */
function AuthSessionSync() {
  useAuthSessionSync();
  return null;
}

/** Auth0 SPA provider wired to react-router navigation. */
export function Auth0ProviderWithNavigate({ children }: Props) {
  const navigate = useNavigate();

  if (!domain || !clientId || !audience) {
    console.error(
      "Auth0 configuration missing. Set VITE_AUTH0_DOMAIN, VITE_AUTH0_CLIENT_ID, and VITE_AUTH0_AUDIENCE in frontend-v2/.env.",
    );
    return <>{children}</>;
  }

  const onRedirectCallback = (appState?: { returnTo?: string }) => {
    navigate(appState?.returnTo || window.location.pathname);
  };

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        // Native shells get a custom-scheme deep link (see nativeAuth.ts); the
        // browser keeps using the current origin.
        redirect_uri: authRedirectUri(),
        audience,
        // Organization is supplied on `/login` (or invitations / org switch),
        // not here — matches backoffice v1 so users pick their tenant first.
      }}
      useRefreshTokens
      cacheLocation="localstorage"
      // OAuth completes via a custom-scheme deep link, not a WebView navigation.
      skipRedirectCallback={isNativePlatform()}
      onRedirectCallback={onRedirectCallback}
    >
      <NativeAuthBridge />
      <AuthSessionSync />
      {children}
    </Auth0Provider>
  );
}
